import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateField } from '@/components/ui/date-field';
import { FormFooter } from '@/components/ui/form-footer';
import { Input } from '@/components/ui/input';
import { EuroInput } from '../capture/EuroInput';
import { formatAmount, parseAmountInput } from '../money';
import type { Report } from '../reports/types';
import { recordPayment } from './api';
import { ClosingDialog } from './ClosingDialog';
import { fromCents, toCents } from './format';
import type { Payment } from './types';

const today = () => new Date().toISOString().slice(0, 10);

type Failure = { code: string; left?: string };

/** What is still to pay, in whole cents: the reimbursable total less what is already paid. */
export function leftToPay(report: Pick<Report, 'reimbursable' | 'reimbursed'>): number {
  return Math.max(0, toCents(report.reimbursable) - toCents(report.reimbursed));
}

type Values = { amount: string; paidOn: string; reference: string; note: string };

/** The four fields of the form: amount, date, reference (a CRO goes here) and an optional note. */
function PaymentFields({
  values,
  onChange,
  onSubmit,
}: {
  readonly values: Values;
  readonly onChange: {
    setAmount: (value: string) => void;
    setPaidOn: (value: string) => void;
    setReference: (value: string) => void;
    setNote: (value: string) => void;
  };
  readonly onSubmit: () => void;
}) {
  const { t } = useTranslation('closing');
  const { amount, paidOn, reference, note } = values;
  const { setAmount, setPaidOn, setReference, setNote } = onChange;
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <label className="flex flex-col gap-1 text-sm font-semibold">
        {t('payments.amount')}
        <EuroInput value={amount} onChange={(event) => setAmount(event.target.value)} />
      </label>
      <label className="flex flex-col gap-1 text-sm font-semibold">
        {t('payments.date')}
        <DateField value={paidOn} aria-required onChange={setPaidOn} />
      </label>
      <label className="flex flex-col gap-1 text-sm font-semibold">
        {t('payments.reference')}
        <Input
          value={reference}
          maxLength={255}
          placeholder={t('payments.referenceHint')}
          onChange={(event) => setReference(event.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm font-semibold">
        {t('payments.note')}
        <Input value={note} maxLength={2000} onChange={(event) => setNote(event.target.value)} />
      </label>
    </form>
  );
}

/**
 * «Registra rimborso» (spec §1.10): the amount is prefilled with what is left, then a date, a
 * reference (a CRO goes here) and an optional note. A payment above what is left comes back as
 * `OVER_PAYMENT` with `remainingCents`, which the message names.
 */
export function PaymentDialog({
  report,
  onRecorded,
  onClose,
}: {
  readonly report: Report;
  readonly onRecorded: (payment: Payment, report: Report) => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('closing');
  const left = leftToPay(report);
  const [amount, setAmount] = useState(fromCents(left).replace('.', ','));
  const [paidOn, setPaidOn] = useState(today());
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  const submit = async (): Promise<void> => {
    if (busy || paidOn === '') return;
    const parsed = parseAmountInput(amount);
    if (parsed === null || toCents(parsed) <= 0) return setFailure({ code: 'amount' });
    if (reference.trim() === '') return setFailure({ code: 'reference' });
    setBusy(true);
    setFailure(null);
    const text = note.trim();
    const answer = await recordPayment(report.id, {
      amount: parsed,
      paidOn,
      reference: reference.trim(),
      ...(text === '' ? {} : { note: text }),
    });
    setBusy(false);
    if (answer.ok) return onRecorded(answer.value.payment, answer.value.report);
    const { code, remainingCents } = answer.problem;
    setFailure({
      code: code === '' ? 'other' : code,
      ...(remainingCents === undefined ? {} : { left: fromCents(remainingCents) }),
    });
  };

  return (
    <ClosingDialog
      title={t('payments.dialogTitle', { number: report.number })}
      description={`${t('payments.toPay', { amount: formatAmount(report.reimbursable) })} · ${t('payments.already', { amount: formatAmount(report.reimbursed) })}`}
      onClose={onClose}
      actions={
        <FormFooter
          className="w-full"
          reason={paidOn === '' ? t('payments.dateMissing') : null}
          primaryLabel={t('payments.save')}
          primaryDisabled={busy || paidOn === ''}
          onPrimary={() => void submit()}
          cancelLabel={t('payments.cancel')}
          onCancel={onClose}
        />
      }
    >
      <PaymentFields
        values={{ amount, paidOn, reference, note }}
        onChange={{ setAmount, setPaidOn, setReference, setNote }}
        onSubmit={() => void submit()}
      />
      {failure === null ? null : (
        <p role="alert" className="text-destructive text-sm">
          {(t as (key: string, options?: object) => string)(`payments.errors.${failure.code}`, {
            left: formatAmount(failure.left ?? fromCents(left)),
          })}
        </p>
      )}
    </ClosingDialog>
  );
}
